import { uploadAvatar } from '../services/chatAgentService';

export interface AvatarUploadPayload {
  fileName: string;
  base64Content: string;
}

// Fetches a static asset and encodes it as base64, for uploading the app's
// default avatar image when the user hasn't set their own.
export const fetchAssetAsBase64 = async (url: string): Promise<string> => {
  const arrayBuffer = await fetch(url).then(response => response.arrayBuffer());
  return btoa(
    new Uint8Array(arrayBuffer).reduce(
      (data, byte) => data + String.fromCharCode(byte),
      ''
    )
  );
};

interface ResolveCreateAvatarDataParams {
  isDirty: boolean;
  dataUrl: string | undefined;
  fileName: string;
  defaultAssetUrl: string;
  defaultAssetFileName: string;
}

// Resolves what to upload for one avatar field during chat agent CREATE:
// the user's staged image if they set one, otherwise the app's default
// asset for that field — encoded as base64 either way, ready to upload
// once a real agent id exists.
export const resolveCreateAvatarData = async ({
  isDirty,
  dataUrl,
  fileName,
  defaultAssetUrl,
  defaultAssetFileName,
}: ResolveCreateAvatarDataParams): Promise<AvatarUploadPayload> => {
  if (isDirty && dataUrl) {
    return { fileName, base64Content: dataUrl.split(',')[1] };
  }
  return {
    fileName: defaultAssetFileName,
    base64Content: await fetchAssetAsBase64(defaultAssetUrl),
  };
};

interface ResolveEditAvatarUrlParams {
  isDirty: boolean;
  dataUrl: string | undefined;
  fileName: string;
  defaultAssetUrl: string;
  defaultAssetFileName: string;
  userId: string;
  agentId: string;
}

// Resolves the final URL to persist for one avatar field during chat agent
// EDIT: uploads the user's staged image if they set one — falling through
// to the default-asset upload below if that upload doesn't return a URL —
// keeps the existing URL if untouched, or uploads the app's default asset
// as a last resort. Returns undefined only if even the default-asset
// upload fails to return a URL.
export const resolveEditAvatarUrl = async ({
  isDirty,
  dataUrl,
  fileName,
  defaultAssetUrl,
  defaultAssetFileName,
  userId,
  agentId,
}: ResolveEditAvatarUrlParams): Promise<string | undefined> => {
  if (isDirty && dataUrl) {
    const uploaded = await uploadAvatar(
      fileName,
      dataUrl.split(',')[1],
      userId,
      agentId
    );
    const uploadedUrl = uploaded?.message?.file_url;
    if (uploadedUrl) return uploadedUrl;
  } else if (!isDirty && dataUrl) {
    return dataUrl;
  }

  const base64Content = await fetchAssetAsBase64(defaultAssetUrl);
  const defaultAvatar = await uploadAvatar(
    defaultAssetFileName,
    base64Content,
    userId,
    agentId
  );
  return defaultAvatar?.message?.file_url;
};
